import Image from '@components/Image';
import { ImgSVG } from '@components/ImgSVG';
import useEditingStore from '@hooks/useEditingStore';
import { $curSandTable } from '@stores/dataStores';
import {
  $GateSandtableGroup,
  $GateSandtableGroupSettings,
} from '@stores/editingMarkerStores';
import {
  ImageType,
  type SandTableImageGroupItem,
  type SandTableMapGroupItem,
} from '@types';
import { getSysUrl } from '@utils/urlMakers';
import { useStore, useStoreMap } from 'effector-react';
import produce from 'immer';
import { memo, useMemo, useState } from 'react';
import { SANDTABLE_GROUP_SETTINGS } from '../constants';

type ItemData = Pick<
  SandTableMapGroupItem | SandTableImageGroupItem,
  'id' | 'name' | 'icon'
>;

export function GroupWidget() {
  const origList = useStoreMap({
    store: $curSandTable,
    keys: [],
    fn: (state) =>
      (state?.groupList || []).map((x) => ({
        id: x.id,
        name: x.name,
        icon: x.icon,
      })),
  });

  const editingGroup = useStore($GateSandtableGroup.state);

  const groupList = useMemo(() => {
    if (!editingGroup) return origList;
    const { index, data: editingData } = editingGroup;
    return produce(origList, (draft) => {
      draft[index] = editingData;
    });
  }, [origList, editingGroup]);

  const groupSettings = useEditingStore({
    $data: $curSandTable,
    dataFn: (state) => state?.groupSettings || {},
    $editing: $GateSandtableGroupSettings.state,
  });

  if (!groupList.length) return null;

  const {
    type = SANDTABLE_GROUP_SETTINGS.type,
    bgColor = SANDTABLE_GROUP_SETTINGS.bgColor,
    fontColor = SANDTABLE_GROUP_SETTINGS.fontColor,
    activeFontColor = SANDTABLE_GROUP_SETTINGS.activeFontColor,
  } = groupSettings;

  return (
    <div
      className="absolute bottom-30 rounded p-10 flex items-center select-none gap-10"
      style={{
        backgroundColor: `rgba(${bgColor})`,
        backdropFilter: 'blur(10px)',
      }}
    >
      {groupList.map((item) => (
        <SandtableGroupItem
          key={item.id}
          item={item}
          color={fontColor}
          activeColor={activeFontColor}
          showIcon={type === 1}
        />
      ))}
    </div>
  );
}

export const SandtableGroupItem = memo(
  ({
    item,
    showIcon,
    color,
    activeColor,
  }: {
    item: ItemData;
    showIcon: boolean;
    color: string;
    activeColor: string;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const colorString = `rgba(${isHovered ? activeColor : color})`;

    const renderIcon = (
      icon: SandTableImageGroupItem['icon'] | SandTableMapGroupItem['icon']
    ) => {
      if (icon.type === 1) {
        return (
          <Image
            src={icon.url}
            type={ImageType.media}
            className="w-24 h-24 mb-5"
            objectFit="fill"
          />
        );
      }
      if (icon.type === 2) {
        return (
          <ImgSVG
            className="w-24 h-24 mb-5"
            src={getSysUrl(icon.url)}
            style={{
              fill: colorString,
            }}
          />
        );
      }
    };

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="flex items-center justify-center flex-col text-sm"
      >
        {showIcon && renderIcon(item.icon)}
        <div
          className="w-60 truncate text-center"
          title={item.name}
          style={{ color: colorString }}
        >
          {item.name || '\u200B'}
        </div>
      </div>
    );
  }
);
